import { timerCatalogItems } from "./TimerCatalog";
import dico from "../../../util/src/language/dico.js";
import type { BehaviorDraggedNodeTemplate } from "../../../util/src/blueprint/types.js";
import type { BehaviorNode } from "../../../util/src/blueprint/index.js";
import type {
  DataSourceId,
  LayoutNode,
} from "../display-editor/layout-types.js";
import type { BehaviorCanvasScope } from "./BehaviorCanvasScope.js";
import { getCustomDisciplineBehaviorTemplateCatalog } from "./CustomDisciplineBehaviorTemplateCatalog.js";
import {
  CONTROL_NODE_TEMPLATES as CONTROL_TEMPLATES,
  OPERATOR_NODE_TEMPLATES,
  VARIABLE_NODE_TEMPLATES,
} from "../../../util/src/blueprint/types.js";
import {
  DATA_SOURCE_PATHS,
  SCALAR_SOURCE_VALUE_TYPES,
  getScalarSourceValueType,
  sourceHasPaths,
} from "../../../util/src/layout/data-source-schema.js";
import { getMockLayoutData } from "../../../util/src/layout/mock.js";
import { getValueTypeColor } from "../../../util/src/blueprint/method.js";
import {
  EDITOR_STYLE_ACTION_PROPERTY_LABEL_KEY,
  EDITOR_STYLE_ACTIONS,
} from "../../../util/src/blueprint/editor-style-actions.js";
import {
  functionGraphHasFunctionBegin,
  functionGraphHasFunctionEnd,
} from "../../../util/src/blueprint/function-graph.js";
import {
  variableGraphHasVariableBegin,
  variableGraphHasVariableEnd,
} from "../../../util/src/blueprint/variable-graph.js";

const LAYOUT_LIST_ROW_ITEM_OBJECT_SOURCE = "layoutListRowItem";
const LAYOUT_LIST_COMPARATOR_OBJECT_SOURCE = "layoutListComparator";
const LAYOUT_LIST_ROW_INDEX_SOURCE = "rowIndex";

const MOCK_LAYOUT_DATA = getMockLayoutData();
// Keep legacy sources readable in saved graphs, without offering redundant
// discipline-specific lists when the category catalogs can be filtered.
const ALL_SOURCES = (Object.keys(DATA_SOURCE_PATHS) as DataSourceId[]).filter(
  (source) =>
    source !== "disciplineAgeCategories" &&
    source !== "disciplineWeightCategories",
);
const LIST_SOURCES = ALL_SOURCES.filter((source) =>
  Array.isArray(MOCK_LAYOUT_DATA[source]),
);
const OBJECT_SOURCES = ALL_SOURCES.filter(
  (source) => sourceHasPaths(source) && !LIST_SOURCES.includes(source),
);
const SCALAR_SOURCES = Object.keys(SCALAR_SOURCE_VALUE_TYPES) as DataSourceId[];

export type BehaviorTemplateCatalogSection =
  | "events"
  | "variables"
  | "controls"
  | "actions"
  | "operators";

export interface BehaviorTemplateCatalogItem {
  id: string;
  label: string;
  section: BehaviorTemplateCatalogSection;
  categoryLabel: string;
  subCategoryLabel?: string;
  dotColor: string;
  template: BehaviorDraggedNodeTemplate;
  isConnectableTarget: boolean;
}

export type { BehaviorCanvasScope } from "./BehaviorCanvasScope.js";

export interface BehaviorTemplateCatalogOptions {
  node: LayoutNode;
  scope?: BehaviorCanvasScope;
  allowRowIndexVariable?: boolean;
  layoutListBindingSource?: DataSourceId;
  connectableOnly?: boolean;
  /** Current scene animation graph nodes; used to hide duplicate animBegin/animEnd templates. */
  sceneGraphNodes?: BehaviorNode[];
}

/**
 * Builds the shared palette catalog used by both the node list and the
 * empty-space connection picker.
 *
 * By default the catalog only returns entries that can realistically act as
 * connection targets. Pass `connectableOnly: false` to retrieve the full palette
 * content with the exact same contextual rules as `BehaviorNodeList`.
 */
export function getBehaviorTemplateCatalog({
  node,
  scope = "node",
  allowRowIndexVariable = false,
  layoutListBindingSource,
  connectableOnly = true,
  sceneGraphNodes,
}: BehaviorTemplateCatalogOptions): BehaviorTemplateCatalogItem[] {
  if (
    scope === "customDiscipline" ||
    scope === "customDisciplineCell" ||
    scope === "customDisciplineModal" ||
    scope === "customDisciplineEntryPolicy" ||
    scope === "customDisciplineReferenceFilter"
  ) {
    return getCustomDisciplineBehaviorTemplateCatalog({
      connectableOnly,
      mode:
        scope === "customDisciplineEntryPolicy"
          ? "entryPolicy"
          : scope === "customDisciplineReferenceFilter"
            ? "referenceFilter"
            : scope === "customDisciplineCell"
              ? "cell"
              : scope === "customDisciplineModal"
                ? "modal"
                : "field",
    });
  }
  const bpDico = dico().behavior;
  const layoutProps = dico().licensedPortal.layoutEditor
    .properties as unknown as Record<string, string>;
  const layoutDataSourcesDico = dico().licensedPortal.layoutEditor
    .dataSources as Record<string, string>;
  const styleNodeLabels =
    ((bpDico.actions as unknown as Record<string, unknown>).styleNodeLabels as
      | Record<string, string>
      | undefined) ?? {};
  const getStyleActionLabel = (
    key: keyof typeof EDITOR_STYLE_ACTION_PROPERTY_LABEL_KEY,
  ) =>
    styleNodeLabels[key] ??
    layoutProps[EDITOR_STYLE_ACTION_PROPERTY_LABEL_KEY[key]] ??
    key;
  const actionsCategoryLabel = bpDico.nodeList.categories.actions;
  const actionSubCategories = bpDico.nodeList.actionSubCategories as Record<
    string,
    string
  >;
  const animationSubCategoryLabel =
    actionSubCategories.animation ?? bpDico.nodeList.categories.animation;
  const allowLayoutListNodeRowIndexVariable =
    scope === "node" &&
    node.kind === "component" &&
    node.component === "layoutList";
  const allowNodeScopeActions = scope === "node" || scope === "functionGraph";
  const allowLayoutListActions =
    allowNodeScopeActions &&
    (scope === "functionGraph" ||
      (node.kind === "component" && node.component === "layoutList"));
  const allowLayoutListContextVariables =
    allowRowIndexVariable ||
    allowLayoutListNodeRowIndexVariable ||
    scope === "functionGraph";
  const layoutListRowBindingSource =
    scope === "node" &&
    layoutListBindingSource &&
    sourceHasPaths(layoutListBindingSource)
      ? layoutListBindingSource
      : undefined;
  const eventOnChangeSources = [
    ...new Set([
      ...SCALAR_SOURCES,
      ...OBJECT_SOURCES,
      ...LIST_SOURCES,
      ...(allowRowIndexVariable
        ? [
            LAYOUT_LIST_ROW_INDEX_SOURCE as DataSourceId,
            LAYOUT_LIST_ROW_ITEM_OBJECT_SOURCE as DataSourceId,
          ]
        : []),
    ]),
  ];
  const items: BehaviorTemplateCatalogItem[] = [];
  const pushItem = (item: BehaviorTemplateCatalogItem) => {
    items.push(item);
  };
  const pushItems = (...nextItems: BehaviorTemplateCatalogItem[]) => {
    items.push(...nextItems);
  };

  if (scope === "node" || scope === "triggerGraph") {
    pushItem({
      id: "event:onLoad",
      label: bpDico.events.kinds.onLoad,
      section: "events",
      categoryLabel: bpDico.nodeList.categories.events,
      subCategoryLabel: bpDico.nodeList.eventSubCategories.lifecycle,
      dotColor: getValueTypeColor("event"),
      template: { type: "event", kind: "onLoad" },
      isConnectableTarget: false,
    });

    eventOnChangeSources.forEach((source) => {
      pushItem({
        id: `event:onChange:${source}`,
        label: layoutDataSourcesDico[source] ?? source,
        section: "events",
        categoryLabel: bpDico.nodeList.categories.events,
        subCategoryLabel: bpDico.nodeList.eventSubCategories.onChange,
        dotColor: getValueTypeColor("event"),
        template: {
          type: "event",
          kind: "onChange",
          objectSource: source,
        },
        isConnectableTarget: false,
      });
    });

    if (scope === "node" || scope === "triggerGraph") {
      const controlRoomSubLabel =
        (bpDico.nodeList.eventSubCategories as Record<string, string>)
          .controlRoom ?? "Control Room";
      pushItem({
        id: "event:onControlRoom",
        label:
          (bpDico.events.kinds as Record<string, string>).onControlRoom ??
          "Control Room",
        section: "events",
        categoryLabel: bpDico.nodeList.categories.events,
        subCategoryLabel: controlRoomSubLabel,
        dotColor: getValueTypeColor("event"),
        template: { type: "event", kind: "onControlRoom" },
        isConnectableTarget: false,
      });
    }

    if (allowRowIndexVariable) {
      const listTransitionSubLabel =
        (bpDico.nodeList.eventSubCategories as Record<string, string>)
          .listTransition ?? "List transition";
      pushItems(
        {
          id: "event:onListEnter",
          label:
            (bpDico.events.kinds as Record<string, string>).onListEnter ??
            "onListEnter",
          section: "events",
          categoryLabel: bpDico.nodeList.categories.events,
          subCategoryLabel: listTransitionSubLabel,
          dotColor: getValueTypeColor("event"),
          template: { type: "event", kind: "onListEnter" },
          isConnectableTarget: false,
        },
        {
          id: "event:onListExit",
          label:
            (bpDico.events.kinds as Record<string, string>).onListExit ??
            "onListExit",
          section: "events",
          categoryLabel: bpDico.nodeList.categories.events,
          subCategoryLabel: listTransitionSubLabel,
          dotColor: getValueTypeColor("event"),
          template: { type: "event", kind: "onListExit" },
          isConnectableTarget: false,
        },
        {
          id: "event:onListFilterExit",
          label:
            (bpDico.events.kinds as Record<string, string>).onListFilterExit ??
            "onListFilterExit",
          section: "events",
          categoryLabel: bpDico.nodeList.categories.events,
          subCategoryLabel: listTransitionSubLabel,
          dotColor: getValueTypeColor("event"),
          template: { type: "event", kind: "onListFilterExit" },
          isConnectableTarget: false,
        },
        {
          id: "event:onListFilterEnter",
          label:
            (bpDico.events.kinds as Record<string, string>).onListFilterEnter ??
            "onListFilterEnter",
          section: "events",
          categoryLabel: bpDico.nodeList.categories.events,
          subCategoryLabel: listTransitionSubLabel,
          dotColor: getValueTypeColor("event"),
          template: { type: "event", kind: "onListFilterEnter" },
          isConnectableTarget: false,
        },
      );
    }
    if (scope === "node" && !allowRowIndexVariable) {
      const listTransitionSubLabel =
        (bpDico.nodeList.eventSubCategories as Record<string, string>)
          .listTransition ?? "List transition";
      pushItems(
        {
          id: "event:onListFilterExit",
          label:
            (bpDico.events.kinds as Record<string, string>).onListFilterExit ??
            "onListFilterExit",
          section: "events",
          categoryLabel: bpDico.nodeList.categories.events,
          subCategoryLabel: listTransitionSubLabel,
          dotColor: getValueTypeColor("event"),
          template: { type: "event", kind: "onListFilterExit" },
          isConnectableTarget: false,
        },
        {
          id: "event:onListFilterEnter",
          label:
            (bpDico.events.kinds as Record<string, string>).onListFilterEnter ??
            "onListFilterEnter",
          section: "events",
          categoryLabel: bpDico.nodeList.categories.events,
          subCategoryLabel: listTransitionSubLabel,
          dotColor: getValueTypeColor("event"),
          template: { type: "event", kind: "onListFilterEnter" },
          isConnectableTarget: false,
        },
      );
    }
  }

  VARIABLE_NODE_TEMPLATES.forEach((template) => {
    pushItem({
      id: `variable:literal:${template.valueType}`,
      label: bpDico.nodeList.inputTypes[template.valueType],
      section: "variables",
      categoryLabel: bpDico.nodeList.categories.variables,
      subCategoryLabel: bpDico.nodeList.variableSubCategories.literal,
      dotColor: getValueTypeColor(template.valueType),
      template,
      isConnectableTarget: false,
    });
  });

  SCALAR_SOURCES.filter(
    (source) => source !== LAYOUT_LIST_ROW_INDEX_SOURCE,
  ).forEach((source) => {
    const valueType = getScalarSourceValueType(source);
    if (!valueType) {
      return;
    }
    pushItem({
      id: `variable:source:${source}`,
      label: layoutDataSourcesDico[source] ?? source,
      section: "variables",
      categoryLabel: bpDico.nodeList.categories.variables,
      subCategoryLabel: bpDico.nodeList.variableSubCategories.source,
      dotColor: getValueTypeColor(valueType),
      template: {
        type: "variable",
        valueType,
        objectSource: source,
      },
      isConnectableTarget: false,
    });
  });

  OBJECT_SOURCES.filter((source) => source !== "currentNode").forEach(
    (source) => {
      pushItem({
        id: `variable:object:${source}`,
        label: layoutDataSourcesDico[source] ?? source,
        section: "variables",
        categoryLabel: bpDico.nodeList.categories.variables,
        subCategoryLabel: bpDico.nodeList.variableSubCategories.object,
        dotColor: getValueTypeColor("object"),
        template: {
          type: "variable",
          valueType: "object",
          objectSource: source,
        },
        isConnectableTarget: false,
      });
    },
  );

  const showContextRowIndex = allowLayoutListContextVariables;
  if (showContextRowIndex) {
    const rowIndexValueType = getScalarSourceValueType(
      LAYOUT_LIST_ROW_INDEX_SOURCE as DataSourceId,
    );
    if (rowIndexValueType) {
      pushItem({
        id: "variable:context:rowIndex",
        label:
          layoutDataSourcesDico[LAYOUT_LIST_ROW_INDEX_SOURCE] ??
          LAYOUT_LIST_ROW_INDEX_SOURCE,
        section: "variables",
        categoryLabel: bpDico.nodeList.categories.variables,
        subCategoryLabel: bpDico.nodeList.variableSubCategories.context,
        dotColor: getValueTypeColor(rowIndexValueType),
        template: {
          type: "variable",
          valueType: rowIndexValueType,
          objectSource: LAYOUT_LIST_ROW_INDEX_SOURCE,
        },
        isConnectableTarget: false,
      });
    }
  }

  pushItem({
    id: "variable:context:currentNode",
    label: layoutDataSourcesDico.currentNode ?? "currentNode",
    section: "variables",
    categoryLabel: bpDico.nodeList.categories.variables,
    subCategoryLabel: bpDico.nodeList.variableSubCategories.context,
    dotColor: getValueTypeColor("object"),
    template: {
      type: "variable",
      valueType: "object",
      objectSource: "currentNode",
    },
    isConnectableTarget: false,
  });

  if (scope === "node") {
    pushItem({
      id: "variable:context:layoutListTarget",
      label: bpDico.actions.setTarget,
      section: "variables",
      categoryLabel: bpDico.nodeList.categories.variables,
      subCategoryLabel: bpDico.nodeList.variableSubCategories.context,
      dotColor: getValueTypeColor("layoutListTarget"),
      template: {
        type: "variable",
        valueType: "layoutListTarget",
      } as unknown as BehaviorDraggedNodeTemplate,
      isConnectableTarget: false,
    });
  }

  if (layoutListRowBindingSource || allowLayoutListContextVariables) {
    pushItem({
      id: "variable:context:currentRow",
      label: bpDico.actions.currentRow,
      section: "variables",
      categoryLabel: bpDico.nodeList.categories.variables,
      subCategoryLabel: bpDico.nodeList.variableSubCategories.context,
      dotColor: getValueTypeColor("object"),
      template: {
        type: "variable",
        valueType: "object",
        objectSource: LAYOUT_LIST_ROW_ITEM_OBJECT_SOURCE,
      } as BehaviorDraggedNodeTemplate,
      isConnectableTarget: false,
    });
  }

  if (layoutListRowBindingSource || scope === "functionGraph") {
    pushItem({
      id: "variable:context:comparator",
      label: bpDico.actions.comparator,
      section: "variables",
      categoryLabel: bpDico.nodeList.categories.variables,
      subCategoryLabel: bpDico.nodeList.variableSubCategories.context,
      dotColor: getValueTypeColor("object"),
      template: {
        type: "variable",
        valueType: "object",
        objectSource: LAYOUT_LIST_COMPARATOR_OBJECT_SOURCE,
      } as unknown as BehaviorDraggedNodeTemplate,
      isConnectableTarget: false,
    });
  }

  if (
    scope === "node" &&
    node.kind === "component" &&
    node.component === "barLoader"
  ) {
    pushItem({
      id: "variable:context:barLoaderDisks",
      label: bpDico.actions.barLoaderDisks,
      section: "variables",
      categoryLabel: bpDico.nodeList.categories.variables,
      subCategoryLabel: bpDico.nodeList.variableSubCategories.context,
      dotColor: getValueTypeColor("object"),
      template: {
        type: "variable",
        valueType: "node",
        objectSource: "barLoaderDisks",
      } as unknown as BehaviorDraggedNodeTemplate,
      isConnectableTarget: false,
    });
  }

  LIST_SOURCES.forEach((source) => {
    pushItem({
      id: `variable:list:${source}`,
      label: layoutDataSourcesDico[source] ?? source,
      section: "variables",
      categoryLabel: bpDico.nodeList.categories.variables,
      subCategoryLabel: bpDico.nodeList.variableSubCategories.lists,
      dotColor: getValueTypeColor("list"),
      template: {
        type: "variable",
        valueType: "list",
        objectSource: source,
      },
      isConnectableTarget: false,
    });
  });

  CONTROL_TEMPLATES.forEach((template) => {
    if (scope === "objectGraph") return;
    if (template.controlType === "eventOutput" && scope !== "triggerGraph")
      return;
    if (
      (template.controlType === "functionBegin" ||
        template.controlType === "functionEnd") &&
      scope !== "functionGraph"
    )
      return;
    if (
      (template.controlType === "variableBegin" ||
        template.controlType === "variableEnd") &&
      scope !== "variableGraph"
    )
      return;
    const hasEventOutput =
      scope === "triggerGraph" &&
      sceneGraphNodes?.some(
        (sceneNode) =>
          sceneNode.type === "control" &&
          sceneNode.controlType === "eventOutput",
      );
    if (template.controlType === "eventOutput" && hasEventOutput) return;
    const hasFunctionBegin =
      scope === "functionGraph" &&
      functionGraphHasFunctionBegin({ nodes: sceneGraphNodes ?? [] });
    const hasFunctionEnd =
      scope === "functionGraph" &&
      functionGraphHasFunctionEnd({ nodes: sceneGraphNodes ?? [] });
    const hasVariableBegin =
      scope === "variableGraph" &&
      variableGraphHasVariableBegin({ nodes: sceneGraphNodes ?? [] });
    const hasVariableEnd =
      scope === "variableGraph" &&
      variableGraphHasVariableEnd({ nodes: sceneGraphNodes ?? [] });
    if (template.controlType === "functionBegin" && hasFunctionBegin) return;
    if (template.controlType === "functionEnd" && hasFunctionEnd) return;
    if (template.controlType === "variableBegin" && hasVariableBegin) return;
    if (template.controlType === "variableEnd" && hasVariableEnd) return;
    pushItem({
      id: `control:${template.controlType}`,
      label: bpDico.controls[template.controlType],
      section: "controls",
      categoryLabel: bpDico.nodeList.categories.controls,
      dotColor: getValueTypeColor("control"),
      template,
      isConnectableTarget: true,
    });
  });

  if (scope === "scene") {
    const hasAnimBegin = sceneGraphNodes?.some(
      (sceneNode) =>
        sceneNode.type === "action" && sceneNode.kind === "animBegin",
    );
    const hasAnimEnd = sceneGraphNodes?.some(
      (sceneNode) =>
        sceneNode.type === "action" && sceneNode.kind === "animEnd",
    );
    (
      [
        { kind: "animBegin", label: bpDico.animation.begin },
        { kind: "animKeyframe", label: bpDico.animation.keyframe },
        { kind: "animEnd", label: bpDico.animation.end },
      ] as const
    ).forEach((entry) => {
      if (entry.kind === "animBegin" && hasAnimBegin) return;
      if (entry.kind === "animEnd" && hasAnimEnd) return;
      pushItem({
        id: `action:${entry.kind}`,
        label: entry.label,
        section: "actions",
        categoryLabel: actionsCategoryLabel,
        subCategoryLabel: animationSubCategoryLabel,
        dotColor: getValueTypeColor("action"),
        template: { type: "action", kind: entry.kind },
        isConnectableTarget: true,
      });
    });
  }

  if (
    scope === "functionGraph" ||
    (scope === "node" && node.kind === "primitive" && node.primitive === "text")
  ) {
    pushItem({
      id: "action:set:text",
      label: bpDico.actions.setText,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "text" },
      isConnectableTarget: true,
    });
  }

  if (allowLayoutListActions) {
    pushItems(
      {
        id: "action:createRowVariant",
        label: bpDico.actions.createRowVariant,
        section: "actions",
        categoryLabel: actionsCategoryLabel,
        subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
        dotColor: getValueTypeColor("action"),
        template: { type: "action", kind: "createRowVariant" },
        isConnectableTarget: true,
      },
      {
        id: "action:set:setListSource",
        label: bpDico.actions.setListSource,
        section: "actions",
        categoryLabel: actionsCategoryLabel,
        subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
        dotColor: getValueTypeColor("action"),
        template: { type: "action", kind: "set", key: "setListSource" },
        isConnectableTarget: true,
      },
      {
        id: "action:set:setFilter",
        label: bpDico.actions.setFilter,
        section: "actions",
        categoryLabel: actionsCategoryLabel,
        subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
        dotColor: getValueTypeColor("action"),
        template: { type: "action", kind: "set", key: "setFilter" },
        isConnectableTarget: true,
      },
      {
        id: "action:set:setSort",
        label: bpDico.actions.setSort,
        section: "actions",
        categoryLabel: actionsCategoryLabel,
        subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
        dotColor: getValueTypeColor("action"),
        template: { type: "action", kind: "set", key: "setSort" },
        isConnectableTarget: true,
      },
    );
  }

  if (allowNodeScopeActions) {
    pushItems(
      {
        id: "action:set:setBinding",
        label: bpDico.actions.setBinding,
        section: "actions",
        categoryLabel: actionsCategoryLabel,
        subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
        dotColor: getValueTypeColor("action"),
        template: { type: "action", kind: "set", key: "setBinding" },
        isConnectableTarget: true,
      },
      {
        id: "action:set:location",
        label: bpDico.actions.setLocation,
        section: "actions",
        categoryLabel: actionsCategoryLabel,
        subCategoryLabel:
          bpDico.nodeList.actionSubCategories.position ??
          layoutProps.positionSize,
        dotColor: getValueTypeColor("action"),
        template: { type: "action", kind: "set", key: "location" },
        isConnectableTarget: true,
      },
      {
        id: "action:set:transform",
        label: bpDico.actions.setTransform,
        section: "actions",
        categoryLabel: actionsCategoryLabel,
        subCategoryLabel:
          bpDico.nodeList.actionSubCategories.position ??
          layoutProps.positionSize,
        dotColor: getValueTypeColor("action"),
        template: { type: "action", kind: "set", key: "transform" },
        isConnectableTarget: true,
      },
    );

    if (
      scope === "node" &&
      node.kind === "component" &&
      node.component === "barLoader"
    ) {
      pushItems(
        {
          id: "action:set:totalWeight",
          label: bpDico.actions.setBarLoaderTotalWeight,
          section: "actions",
          categoryLabel: actionsCategoryLabel,
          subCategoryLabel: actionSubCategories.barLoader ?? "",
          dotColor: getValueTypeColor("number"),
          template: { type: "action", kind: "set", key: "totalWeight" },
          isConnectableTarget: true,
        },
        {
          id: "action:set:barWeight",
          label: bpDico.actions.setBarLoaderBarWeight,
          section: "actions",
          categoryLabel: actionsCategoryLabel,
          subCategoryLabel: actionSubCategories.barLoader ?? "",
          dotColor: getValueTypeColor("number"),
          template: { type: "action", kind: "set", key: "barWeight" },
          isConnectableTarget: true,
        },
      );
    }

    if (
      scope === "node" &&
      node.kind === "component" &&
      node.component === "icon"
    ) {
      pushItems(
        {
          id: "action:set:iconColor",
          label: bpDico.actions.iconColor ?? "Icon color",
          section: "actions",
          categoryLabel: actionsCategoryLabel,
          subCategoryLabel: bpDico.nodeList.actionSubCategories.style,
          dotColor: getValueTypeColor("color"),
          template: { type: "action", kind: "set", key: "iconColor" },
          isConnectableTarget: true,
        },
        {
          id: "action:set:iconSize",
          label: bpDico.actions.iconSize ?? "Icon size",
          section: "actions",
          categoryLabel: actionsCategoryLabel,
          subCategoryLabel: bpDico.nodeList.actionSubCategories.style,
          dotColor: getValueTypeColor("number"),
          template: { type: "action", kind: "set", key: "iconSize" },
          isConnectableTarget: true,
        },
      );
    }

    EDITOR_STYLE_ACTIONS.forEach((definition) => {
      pushItem({
        id: `action:set:${definition.key}`,
        label: getStyleActionLabel(definition.key),
        section: "actions",
        categoryLabel: actionsCategoryLabel,
        subCategoryLabel: bpDico.nodeList.actionSubCategories.style,
        dotColor: getValueTypeColor("action"),
        template: { type: "action", kind: "set", key: definition.key },
        isConnectableTarget: true,
      });
    });
  }

  (
    Object.keys(OPERATOR_NODE_TEMPLATES) as Array<
      keyof typeof OPERATOR_NODE_TEMPLATES
    >
  ).forEach((inputType) => {
    OPERATOR_NODE_TEMPLATES[inputType].forEach((template) => {
      pushItem({
        id: `operator:${inputType}:${template.operator}`,
        label: bpDico.operators[template.operator] ?? String(template.operator),
        section: "operators",
        categoryLabel: bpDico.nodeList.categories.operators,
        subCategoryLabel: bpDico.nodeList.inputTypes[inputType],
        dotColor: getValueTypeColor(inputType),
        template,
        isConnectableTarget: true,
      });
    });
  });

  items.push(
    ...timerCatalogItems(scope === "node" || scope === "functionGraph"),
  );
  return connectableOnly
    ? items.filter((item) => item.isConnectableTarget)
    : items;
}
